import { FC, useEffect, useState } from "react";
import Filters from "../insights/components/Filters";
import type { DateRange } from "@capacit-com/lara-law-shared-ui";
import { DateTime } from "luxon";
import useStatistics from "@/hooks/useStatistics";
import { UserStatisticResponse } from "@/models/statistic/Statistic";
import Statistics from "../insights/components/Statistics";
import UserStatsChart from "./components/UserStatsChart";
import UsersTable from "./components/UsersTable";

const UsersView: FC = () => {
  const { useGetUsersStatistics } = useStatistics();

  const [data, setData] = useState<UserStatisticResponse>();
  const [presetText, setPresetText] = useState<string | undefined>();
  const [presetTextCompared, setPresetTextCompared] = useState<
    string | undefined
  >();

  const defaultDateRange = {
    from: DateTime.now().minus({ days: 7 }).startOf("day").toJSDate(),
    to: DateTime.now().endOf("day").toJSDate(),
  };

  const defaultCompareDateRange = {
    from: DateTime.now().minus({ days: 30 }).startOf("day").toJSDate(),
    to: DateTime.now().endOf("day").toJSDate(),
  };

  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: defaultDateRange.from,
    to: defaultDateRange.to,
  });

  const [compareDateRange, setCompareDateRange] = useState<
    DateRange | undefined
  >({
    from: defaultCompareDateRange.from,
    to: defaultCompareDateRange.to,
  });

  const fetchedStats = useGetUsersStatistics(
    dateRange?.from?.toISOString() ?? defaultDateRange.from.toISOString(),
    dateRange?.to?.toISOString() ?? defaultDateRange.to.toISOString(),
    compareDateRange?.from?.toISOString() ??
      defaultDateRange.from.toISOString(),
    compareDateRange?.to?.toISOString() ?? defaultDateRange.to.toISOString()
  );

  useEffect(() => {
    setPresetText("Sidste 7 dage");
    setPresetTextCompared("Sidste 30 dage");
  }, []);

  useEffect(() => {
    if (fetchedStats.isFetched && fetchedStats.data) {
      setData(fetchedStats.data);
    }
  }, [fetchedStats.data, fetchedStats.isFetched]);

  const statisticsArray = [
    {
      title: "Samtaler",
      value: data?.totalConversations,
      percentage: data?.conversationPercentageChange,
      tooltip: "Antal samtaler i perioden",
    },
    {
      title: "Beskeder",
      value: data?.totalMessages,
      percentage: data?.messagePercentageChange,
      tooltip: "Antal beskeder i perioden",
    },
    {
      title: "Logins",
      value: data?.totalLogins,
      percentage: data?.loginPercentageChange,
      tooltip: "Antal logins i perioden",
    },
  ];

  return (
    <div className="space-y-4 p-4 xl:px-8 overflow-visible transition-all duration-300">
      <div className="flex flex-row justify-end gap-2 items-end">
        <Filters
          presetText={presetText}
          setPresetText={setPresetText}
          date={dateRange}
          setDate={setDateRange}
        />
        <Filters
          presetText={presetTextCompared}
          setPresetText={setPresetTextCompared}
          titlePrefix="Sammenlignet med"
          date={compareDateRange}
          setDate={setCompareDateRange}
        />
      </div>
      <Statistics
        statisticsConfig={statisticsArray}
        isLoading={fetchedStats.isLoading}
        isError={fetchedStats.isError}
      />
      {data && <UserStatsChart data={data.graphStats} />}
      <div className="overflow-auto border rounded-lg bg-card">
        <UsersTable />
      </div>
    </div>
  );
};

export default UsersView;
